import React, { useCallback, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableSortLabel,
  IconButton,
  Menu,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert,
  Checkbox,
  ListItem,
  ListItemText,
  Tooltip,
} from '@mui/material';
import {
  Add as AddIcon,
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { WithGuideHelp } from '@modules/ui-components';
import { GuideService } from '@modules/firebase-core/services/GuideService';
import { useStudyCases, useTasks } from '../hooks';
import type { StudyCaseFormData, OutputFormData, SortDirection, MinimalTask } from '../types';
import type { StudyCase, Output } from '@segmently/schema-models/entities';

/* ------------------------------------------------------------------ */
/*  Constants                                                         */
/* ------------------------------------------------------------------ */

const EMPTY_FORM: StudyCaseFormData = {
  featureName: '',
  featureDescription: '',
  minTheses: 1,
  preferredSources: [],
  maxStudyAgeYears: 1,
  outputLanguage: '',
  primaryBenefit: '',
  outputs: [],
  selectedTaskIds: [],
};

const EMPTY_OUTPUT: OutputFormData = {
  shortDescription: '',
  mainSense: '',
  linkToStudy: '',
};

const BACKDROP_SX = {
  backgroundColor: 'rgba(0, 0, 0, 0.15)',
  backdropFilter: 'blur(20px)',
};

// iOS nav bar layout — close LEFT, title CENTRE, phantom RIGHT.
const DIALOG_TITLE_SX = {
  display: 'grid',
  gridTemplateColumns: '40px 1fr 40px',
  alignItems: 'center',
  gap: 1,
  padding: '0 0 8px',
};

const DIALOG_CONTENT_SX = {
  padding: '0',
  my: 2,
};

const DIALOG_ACTIONS_SX = { p: '16px 0 0' };

/* ------------------------------------------------------------------ */
/*  Component                                                         */
/* ------------------------------------------------------------------ */

export default function StudyCasesTable({ projectId }: { projectId: string }) {
  const {
    studyCases,
    loading,
    error,
    setError,
    createStudyCase,
    updateStudyCase,
    deleteStudyCase,
    getOutputsService,
  } = useStudyCases(projectId);

  const tasks = useTasks(projectId);

  /* ---------- bulk selection ---------- */
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const s = new Set(prev);
      if (s.has(id)) s.delete(id); else s.add(id);
      return s;
    });
  }, []);

  const handleSelectAll = useCallback(() => {
    if (selectedIds.size === studyCases.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(studyCases.map(r => r.id!).filter(Boolean)));
    }
  }, [selectedIds.size, studyCases]);

  const handleDeleteSelected = useCallback(async () => {
    if (selectedIds.size === 0) return;
    try {
      await Promise.all(Array.from(selectedIds).map(id => deleteStudyCase(id)));
      setSelectedIds(new Set());
    } catch (err) {
      console.error('Error deleting selected study cases:', err);
      setError('Failed to delete selected study cases.');
    }
  }, [selectedIds, deleteStudyCase, setError]);

  /* ---------- sorting ---------- */
  const [sortField, setSortField] = useState<string>('featureName');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedRows = useMemo(() => {
    const s = [...studyCases];
    s.sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      const key = sortField as keyof StudyCase;

      if (['minTheses', 'maxStudyAgeYears'].includes(sortField)) {
        av = Number((a as unknown as Record<string, unknown>)[sortField]) || 0;
        bv = Number((b as unknown as Record<string, unknown>)[sortField]) || 0;
      } else if (sortField === 'preferredSources') {
        av = Array.isArray((a as unknown as Record<string, unknown>)[sortField])
          ? ((a as unknown as Record<string, unknown>)[sortField] as string[]).length
          : 0;
        bv = Array.isArray((b as unknown as Record<string, unknown>)[sortField])
          ? ((b as unknown as Record<string, unknown>)[sortField] as string[]).length
          : 0;
      } else {
        av = ((a as unknown as Record<string, unknown>)[key] || '').toString().toLowerCase();
        bv = ((b as unknown as Record<string, unknown>)[key] || '').toString().toLowerCase();
      }

      if (av < bv) return sortDirection === 'asc' ? -1 : 1;
      if (av > bv) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
    return s;
  }, [studyCases, sortField, sortDirection]);

  /* ---------- context menu ---------- */
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [selectedRow, setSelectedRow] = useState<StudyCase | null>(null);

  const openMenu = (e: React.MouseEvent<HTMLButtonElement>, row: StudyCase) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
    setSelectedRow(row);
  };

  const closeMenu = () => {
    setAnchorEl(null);
    setSelectedRow(null);
  };

  /* ---------- main dialog ---------- */
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [formData, setFormData] = useState<StudyCaseFormData>({ ...EMPTY_FORM });

  const guideService = useMemo(() => new GuideService(), []);

  const loadOutputsForRow = async (rowId: string): Promise<OutputFormData[]> => {
    try {
      const outputService = getOutputsService(rowId);
      const outputs = await outputService.list();
      return outputs.map((o) => {
        const raw = o as unknown as Record<string, unknown>;
        return {
          shortDescription: (raw.shortDescription as string) ?? '',
          mainSense: (raw.mainSense as string) ?? '',
          linkToStudy: (raw.linkToStudy as string) ?? '',
        };
      });
    } catch (err) {
      console.error('Load outputs failed:', err);
      return [];
    }
  };

  const handleRowClick = async (row: StudyCase) => {
    setSelectedRow(row);
    setDialogMode('edit');

    const outputs = await loadOutputsForRow(row.id!);

    setFormData({
      featureName: row.featureName ?? '',
      featureDescription: row.featureDescription ?? '',
      minTheses: row.minTheses ?? 1,
      preferredSources: Array.isArray(row.preferredSources) ? row.preferredSources : [],
      maxStudyAgeYears: row.maxStudyAgeYears ?? 1,
      outputLanguage: row.outputLanguage ?? '',
      primaryBenefit: row.primaryBenefit ?? '',
      outputs,
      selectedTaskIds: Array.isArray((row as unknown as Record<string, unknown>).taskIds)
        ? ((row as unknown as Record<string, unknown>).taskIds as string[])
        : Array.isArray(row.selectedTaskIds)
          ? row.selectedTaskIds
          : [],
    });
    setOpenDialog(true);
  };

  const handleCreate = () => {
    setDialogMode('create');
    setFormData({ ...EMPTY_FORM, outputs: [], preferredSources: [], selectedTaskIds: [] });
    setOpenDialog(true);
  };

  const handleEdit = async () => {
    if (!selectedRow) return;
    setDialogMode('edit');

    const outputs = await loadOutputsForRow(selectedRow.id!);

    setFormData({
      featureName: selectedRow.featureName ?? '',
      featureDescription: selectedRow.featureDescription ?? '',
      minTheses: selectedRow.minTheses ?? 1,
      preferredSources: Array.isArray(selectedRow.preferredSources) ? selectedRow.preferredSources : [],
      maxStudyAgeYears: selectedRow.maxStudyAgeYears ?? 1,
      outputLanguage: selectedRow.outputLanguage ?? '',
      primaryBenefit: selectedRow.primaryBenefit ?? '',
      outputs,
      selectedTaskIds: Array.isArray((selectedRow as unknown as Record<string, unknown>).taskIds)
        ? ((selectedRow as unknown as Record<string, unknown>).taskIds as string[])
        : Array.isArray(selectedRow.selectedTaskIds)
          ? selectedRow.selectedTaskIds
          : [],
    });
    setOpenDialog(true);
    closeMenu();
  };

  const handleDelete = async () => {
    if (!selectedRow) return;
    try {
      await deleteStudyCase(selectedRow.id!);
      closeMenu();
    } catch (err) {
      console.error('Delete study case failed:', err);
      setError('Failed to delete study case.');
    }
  };

  const handleSave = async () => {
    try {
      const payload: Partial<StudyCase> = {
        featureName: (formData.featureName || '').toString(),
        featureDescription: (formData.featureDescription || '').toString(),
        minTheses: Math.max(1, Number(formData.minTheses) || 1),
        preferredSources: formData.preferredSources || [],
        maxStudyAgeYears: Math.max(1, Number(formData.maxStudyAgeYears) || 1),
        outputLanguage: (formData.outputLanguage || '').toString(),
        primaryBenefit: (formData.primaryBenefit || '').toString(),
        selectedTaskIds: Array.isArray(formData.selectedTaskIds) ? formData.selectedTaskIds : [],
      };

      let studyCaseId: string;
      if (dialogMode === 'create') {
        const created = await createStudyCase(payload);
        studyCaseId = created.id;
      } else {
        studyCaseId = selectedRow!.id!;
        await updateStudyCase(studyCaseId, payload);
      }

      // Persist outputs subcollection
      if (formData.outputs && formData.outputs.length > 0) {
        const outputService = getOutputsService(studyCaseId);

        // Delete existing outputs
        const existing = await outputService.list();
        await Promise.all(existing.map((o) => outputService.delete(o.id)));

        // Re-create from form data
        await Promise.all(
          formData.outputs.map((output) =>
            outputService.create({
              shortDescription: (output.shortDescription || '').toString(),
              mainSense: (output.mainSense || '').toString(),
              linkToStudy: (output.linkToStudy || '').toString(),
            } as unknown as Partial<Output>),
          ),
        );
      } else if (dialogMode === 'edit') {
        // If outputs were cleared, delete all existing
        const outputService = getOutputsService(studyCaseId);
        const existing = await outputService.list();
        await Promise.all(existing.map((o) => outputService.delete(o.id)));
      }

      setOpenDialog(false);
      setSelectedRow(null);
    } catch (err) {
      console.error('Save study case failed:', err);
      setError('Failed to save study case.');
    }
  };

  const handleCancel = () => {
    setOpenDialog(false);
    setFormData({ ...EMPTY_FORM, outputs: [], preferredSources: [], selectedTaskIds: [] });
  };

  /* ---------- output management ---------- */
  const [outputForm, setOutputForm] = useState<OutputFormData>({ ...EMPTY_OUTPUT });
  const [editingOutputIndex, setEditingOutputIndex] = useState<number | null>(null);
  const [openOutputDialog, setOpenOutputDialog] = useState(false);
  const [outputMenuAnchorEl, setOutputMenuAnchorEl] = useState<HTMLElement | null>(null);
  const [outputMenuIndex, setOutputMenuIndex] = useState<number | null>(null);

  const handleAddOutput = () => {
    setEditingOutputIndex(null);
    setOutputForm({ ...EMPTY_OUTPUT });
    setOpenOutputDialog(true);
  };

  const handleEditOutput = (index: number) => {
    const output = formData.outputs[index];
    setOutputForm({
      shortDescription: output.shortDescription || '',
      mainSense: output.mainSense || '',
      linkToStudy: output.linkToStudy || '',
    });
    setEditingOutputIndex(index);
    setOpenOutputDialog(true);
  };

  const handleDeleteOutput = (index: number) => {
    const updatedOutputs = formData.outputs.filter((_, i) => i !== index);
    setFormData({ ...formData, outputs: updatedOutputs });
    if (editingOutputIndex === index) {
      setEditingOutputIndex(null);
      setOutputForm({ ...EMPTY_OUTPUT });
    } else if (editingOutputIndex !== null && editingOutputIndex > index) {
      setEditingOutputIndex(editingOutputIndex - 1);
    }
  };

  const handleSaveOutput = () => {
    if (!outputForm.shortDescription.trim()) return;

    const newOutput: OutputFormData = {
      shortDescription: outputForm.shortDescription.trim(),
      mainSense: outputForm.mainSense.trim(),
      linkToStudy: outputForm.linkToStudy.trim(),
    };

    if (editingOutputIndex !== null) {
      const updatedOutputs = [...formData.outputs];
      updatedOutputs[editingOutputIndex] = newOutput;
      setFormData({ ...formData, outputs: updatedOutputs });
    } else {
      setFormData({ ...formData, outputs: [...formData.outputs, newOutput] });
    }

    setOpenOutputDialog(false);
    setEditingOutputIndex(null);
    setOutputForm({ ...EMPTY_OUTPUT });
  };

  const handleCancelOutput = () => {
    setOpenOutputDialog(false);
    setEditingOutputIndex(null);
    setOutputForm({ ...EMPTY_OUTPUT });
  };

  const handleOutputMenuOpen = (event: React.MouseEvent<HTMLButtonElement>, index: number) => {
    event.stopPropagation();
    setOutputMenuAnchorEl(event.currentTarget);
    setOutputMenuIndex(index);
  };

  const handleOutputMenuClose = () => {
    setOutputMenuAnchorEl(null);
    setOutputMenuIndex(null);
  };

  /* ---------- preferred sources chip input ---------- */
  const [preferredSourceInput, setPreferredSourceInput] = useState('');

  const handleAddPreferredSource = () => {
    if (!preferredSourceInput.trim()) return;
    setFormData((prev) => ({
      ...prev,
      preferredSources: [...prev.preferredSources, preferredSourceInput.trim()],
    }));
    setPreferredSourceInput('');
  };

  const handleRemovePreferredSource = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      preferredSources: prev.preferredSources.filter((_, i) => i !== index),
    }));
  };

  /* ---------- task selection dialog ---------- */
  const [openTaskSelectionDialog, setOpenTaskSelectionDialog] = useState(false);
  const [tempSelectedTaskIds, setTempSelectedTaskIds] = useState<string[]>([]);

  const handleOpenTaskSelection = () => {
    setTempSelectedTaskIds([...formData.selectedTaskIds]);
    setOpenTaskSelectionDialog(true);
  };

  const handleCloseTaskSelection = () => {
    setOpenTaskSelectionDialog(false);
  };

  const handleConfirmTaskSelection = () => {
    setFormData({ ...formData, selectedTaskIds: tempSelectedTaskIds });
    setOpenTaskSelectionDialog(false);
  };

  const handleTaskToggle = (taskId: string) => {
    setTempSelectedTaskIds((prev) =>
      prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId],
    );
  };

  const handleRemoveTask = (taskId: string) => {
    setFormData((prev) => ({
      ...prev,
      selectedTaskIds: prev.selectedTaskIds.filter((id) => id !== taskId),
    }));
  };

  /* ------------------------------------------------------------------ */
  /*  Render                                                            */
  /* ------------------------------------------------------------------ */

  return (
    <Box sx={{ mt: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6" gutterBottom>
            Study Cases
          </Typography>
          {selectedIds.size > 0 && (
            <>
              <Chip label={selectedIds.size} size="small" color="primary" sx={{ fontWeight: 'medium' }} />
              <Tooltip title="Delete selected">
                <IconButton onClick={handleDeleteSelected} color="error" size="small" aria-label="Delete selected">
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </>
          )}
        </Box>
        <WithGuideHelp guideKey="study-cases-create-button" guideService={guideService} position="top-right">
          <Button
            data-testid="studycases-create-button"
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreate}
            size="small"
            disabled={!projectId}
          >
            Create Study Case
          </Button>
        </WithGuideHelp>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Main table */}
      <TableContainer>
        <Table data-testid="studycases-table" sx={{ minWidth: 650, tableLayout: 'auto', width: '100%' }}>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={studyCases.length > 0 && selectedIds.size === studyCases.length}
                  indeterminate={selectedIds.size > 0 && selectedIds.size < studyCases.length}
                  onChange={handleSelectAll}
                  disabled={studyCases.length === 0}
                  size="small"
                />
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', minWidth: 160 }}>
                <TableSortLabel
                  active={sortField === 'featureName'}
                  direction={sortField === 'featureName' ? sortDirection : 'asc'}
                  onClick={() => handleSort('featureName')}
                >
                  Feature Name
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>
                <TableSortLabel
                  active={sortField === 'featureDescription'}
                  direction={sortField === 'featureDescription' ? sortDirection : 'asc'}
                  onClick={() => handleSort('featureDescription')}
                >
                  Study short description
                </TableSortLabel>
              </TableCell>
              <TableCell sx={{ fontWeight: 'bold', width: 180 }} align="right">
                Actions
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedRows.length > 0 ? (
              sortedRows.map((row) => (
                <TableRow
                  key={row.id}
                  data-testid={`studycases-row-${row.id}`}
                  hover
                  onClick={() => handleRowClick(row)}
                  sx={{ cursor: 'pointer' }}
                  selected={selectedIds.has(row.id!)}
                >
                  <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selectedIds.has(row.id!)}
                      onChange={() => handleToggleSelect(row.id!)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell
                    sx={{ whiteSpace: 'nowrap', maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis' }}
                  >
                    {row.featureName ?? ''}
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'normal', wordBreak: 'break-word', maxWidth: 320 }}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {Array.isArray((row as unknown as Record<string, unknown>).outputs) &&
                      ((row as unknown as Record<string, unknown>).outputs as unknown[]).length > 0
                        ? ((row as unknown as Record<string, unknown>).outputs as Array<string | Output>).map(
                            (item, idx) => (
                              <Chip
                                key={idx}
                                label={typeof item === 'string' ? item : item.shortDescription}
                                size="small"
                              />
                            ),
                          )
                        : 'None'}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" onClick={(e) => openMenu(e, row)}>
                      <MoreVertIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No study cases found. Click &quot;Create Study Case&quot; to add one.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Context menu */}
      <Menu
        data-testid="studycases-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        disablePortal
        PaperProps={{ sx: { minWidth: 180 } }}
      >
        <MenuItem onClick={handleEdit}>
          <EditIcon sx={{ mr: 1 }} />
          Edit
        </MenuItem>
        <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
          <DeleteIcon sx={{ mr: 1 }} />
          Delete
        </MenuItem>
      </Menu>

      {/* ============================================================ */}
      {/*  Main Create / Edit Dialog                                   */}
      {/* ============================================================ */}
      <Dialog
        data-testid="studycases-dialog"
        open={openDialog}
        onClose={handleCancel}
        maxWidth="lg"
        fullWidth
        slotProps={{ backdrop: { sx: BACKDROP_SX } }}
      >
        <DialogTitle sx={DIALOG_TITLE_SX}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <IconButton
              onClick={handleCancel}
              size="small"
              aria-label="close dialog"
              sx={{ backgroundColor: (theme) => theme.palette.action.hover }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            component="span"
            sx={{
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {dialogMode === 'create' ? 'Create Study Case' : 'Edit Study Case'}
          </Box>
          <Box />
        </DialogTitle>

        <DialogContent sx={DIALOG_CONTENT_SX}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Feature Name"
              value={formData.featureName}
              onChange={(e) => setFormData({ ...formData, featureName: e.target.value })}
              fullWidth
              inputProps={{ 'data-testid': 'studycases-field-featureName' }}
            />
            <TextField
              label="Feature Description"
              value={formData.featureDescription}
              onChange={(e) => setFormData({ ...formData, featureDescription: e.target.value })}
              fullWidth
              multiline
              rows={3}
              inputProps={{ 'data-testid': 'studycases-field-featureDescription' }}
            />
            <TextField
              label="Min Theses"
              type="number"
              value={formData.minTheses}
              onChange={(e) =>
                setFormData({ ...formData, minTheses: Math.max(1, parseInt(e.target.value) || 1) })
              }
              slotProps={{ htmlInput: { min: 1 } }}
              fullWidth
            />

            {/* Preferred Sources */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Preferred Sources
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Add preferred source"
                  value={preferredSourceInput}
                  onChange={(e) => setPreferredSourceInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddPreferredSource();
                    }
                  }}
                />
                <Button variant="contained" onClick={handleAddPreferredSource} sx={{ ml: 1 }}>
                  Add
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.preferredSources.map((source, idx) => (
                  <Chip key={idx} label={source} onDelete={() => handleRemovePreferredSource(idx)} />
                ))}
              </Box>
            </Box>

            <TextField
              label="Max Study Age"
              type="number"
              value={formData.maxStudyAgeYears}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  maxStudyAgeYears: Math.max(1, parseInt(e.target.value) || 1),
                })
              }
              slotProps={{ htmlInput: { min: 1 } }}
              fullWidth
            />
            <TextField
              label="Output Language"
              value={formData.outputLanguage}
              onChange={(e) => setFormData({ ...formData, outputLanguage: e.target.value })}
              fullWidth
              inputProps={{ 'data-testid': 'studycases-field-outputLanguage' }}
            />
            <TextField
              label="Primary Benefit"
              value={formData.primaryBenefit}
              onChange={(e) => setFormData({ ...formData, primaryBenefit: e.target.value })}
              fullWidth
              inputProps={{ 'data-testid': 'studycases-field-primaryBenefit' }}
            />

            {/* Tasks Selection */}
            <Box>
              <Typography variant="subtitle2" gutterBottom>
                Tasks
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <Button variant="contained" onClick={handleOpenTaskSelection} size="small">
                  Select Tasks
                </Button>
              </Box>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {formData.selectedTaskIds.map((taskId) => {
                  const task = tasks.find((t: MinimalTask) => t.id === taskId);
                  return (
                    <Chip
                      key={taskId}
                      label={task ? task.name : taskId}
                      onDelete={() => handleRemoveTask(taskId)}
                      size="small"
                    />
                  );
                })}
              </Box>
            </Box>

            {/* Outputs Section */}
            <Box sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" gutterBottom>
                  Outputs
                </Typography>
                <Button
                  data-testid="studycases-add-output-button"
                  variant="outlined"
                  size="small"
                  onClick={handleAddOutput}
                  startIcon={<AddIcon />}
                >
                  Add Output
                </Button>
              </Box>

              {formData.outputs && formData.outputs.length > 0 ? (
                <TableContainer sx={{ mb: 2 }}>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Short Description</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Main Sense</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Link to Study</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }} align="right">
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {formData.outputs.map((output, index) => (
                        <TableRow
                          key={index}
                          onClick={() => handleEditOutput(index)}
                          sx={{
                            cursor: 'pointer',
                            '&:hover': { backgroundColor: 'action.hover' },
                          }}
                        >
                          <TableCell sx={{ maxWidth: 300, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {output.shortDescription}
                          </TableCell>
                          <TableCell>{output.mainSense}</TableCell>
                          <TableCell sx={{ maxWidth: 300, whiteSpace: 'normal', wordBreak: 'break-word' }}>
                            {output.linkToStudy}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ position: 'relative' }}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <IconButton
                              size="small"
                              onClick={(e) => handleOutputMenuOpen(e, index)}
                              sx={{ zIndex: 2, position: 'relative' }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                            <Menu
                              anchorEl={outputMenuAnchorEl}
                              open={outputMenuIndex === index && Boolean(outputMenuAnchorEl)}
                              onClose={handleOutputMenuClose}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                              PaperProps={{ sx: { minWidth: 180, zIndex: 1300 } }}
                              sx={{ zIndex: 1300 }}
                            >
                              <MenuItem
                                onClick={() => {
                                  handleEditOutput(index);
                                  handleOutputMenuClose();
                                }}
                              >
                                <EditIcon sx={{ mr: 1 }} />
                                Edit
                              </MenuItem>
                              <MenuItem
                                onClick={() => {
                                  handleDeleteOutput(index);
                                  handleOutputMenuClose();
                                }}
                                sx={{ color: 'error.main' }}
                              >
                                <DeleteIcon sx={{ mr: 1 }} />
                                Delete
                              </MenuItem>
                            </Menu>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No outputs added.
                </Typography>
              )}
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={DIALOG_ACTIONS_SX}>
          <Button data-testid="studycases-save-button" onClick={handleSave} variant="contained">
            {dialogMode === 'create' ? 'Create' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/*  Output Dialog                                               */}
      {/* ============================================================ */}
      <Dialog
        data-testid="studycases-output-dialog"
        open={openOutputDialog}
        onClose={handleCancelOutput}
        maxWidth="md"
        fullWidth
        slotProps={{ backdrop: { sx: BACKDROP_SX } }}
      >
        <DialogTitle sx={DIALOG_TITLE_SX}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <IconButton
              onClick={handleCancelOutput}
              size="small"
              aria-label="close dialog"
              sx={{ backgroundColor: (theme) => theme.palette.action.hover }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            component="span"
            sx={{
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {editingOutputIndex !== null ? 'Edit Output' : 'Add Output'}
          </Box>
          <Box />
        </DialogTitle>

        <DialogContent sx={DIALOG_CONTENT_SX}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Short Description"
              value={outputForm.shortDescription}
              onChange={(e) => setOutputForm({ ...outputForm, shortDescription: e.target.value })}
              fullWidth
              multiline
              rows={3}
              required
            />
            <TextField
              label="Main Sense"
              value={outputForm.mainSense}
              onChange={(e) => setOutputForm({ ...outputForm, mainSense: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="Link to Study"
              value={outputForm.linkToStudy}
              onChange={(e) => setOutputForm({ ...outputForm, linkToStudy: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={DIALOG_ACTIONS_SX}>
          <Button
            onClick={handleSaveOutput}
            variant="contained"
            disabled={!outputForm.shortDescription.trim()}
          >
            {editingOutputIndex !== null ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ============================================================ */}
      {/*  Task Selection Dialog                                       */}
      {/* ============================================================ */}
      <Dialog
        data-testid="studycases-task-dialog"
        open={openTaskSelectionDialog}
        onClose={handleCloseTaskSelection}
        maxWidth="sm"
        fullWidth
        slotProps={{ backdrop: { sx: BACKDROP_SX } }}
      >
        <DialogTitle sx={DIALOG_TITLE_SX}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <IconButton
              onClick={handleCloseTaskSelection}
              size="small"
              aria-label="close dialog"
              sx={{ backgroundColor: (theme) => theme.palette.action.hover }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box
            component="span"
            sx={{
              textAlign: 'center',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            Select Tasks
          </Box>
          <Box />
        </DialogTitle>

        <DialogContent sx={DIALOG_CONTENT_SX}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {tasks.map((task: MinimalTask) => (
              <ListItem key={task.id} dense>
                <Checkbox
                  edge="start"
                  checked={tempSelectedTaskIds.includes(task.id)}
                  onChange={() => handleTaskToggle(task.id)}
                  tabIndex={-1}
                  disableRipple
                />
                <ListItemText primary={task.name} />
              </ListItem>
            ))}
          </Box>
        </DialogContent>

        <DialogActions sx={DIALOG_ACTIONS_SX}>
          <Button onClick={handleConfirmTaskSelection} variant="contained">
            Done
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
